import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Plus, Trash2, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../lib/api';
import { CLIENT_FACING_FIELDS, ASPECT_RATIOS, DELIVERABLE_PLATFORMS, EDIT_LENGTHS, formatEditLength, emptyDeliverable } from '../lib/briefHelpers';
import { CheckboxGroup } from '../components/CheckboxGroup';
import { Logo } from '../components/Logo';

// Client-facing pages fill their fields with parchment rather than the app's mist.
const FIELD = 'bg-parchment';

export default function PublicBriefFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [production, setProduction] = useState({});
  const [shootDates, setShootDates] = useState('');
  const [deliverables, setDeliverables] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/public/briefs/${token}`);
        setBrief(res.data);
        setShootDates((res.data.shoot_dates || []).join(', '));
        // Prefill the producer's initial answers so the client can review/replace them
        const f = res.data.fields || {};
        const initialProd = {};
        CLIENT_FACING_FIELDS.forEach((fd) => {
          if (f[fd.key] != null) initialProd[fd.key] = f[fd.key];
        });
        setProduction(initialProd);
        setDeliverables(res.data.deliverables?.length ? res.data.deliverables : [emptyDeliverable()]);
        if (res.data.already_submitted) setSubmitted(true);
      } catch (e) {
        setError('This brief link is invalid or no longer available.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setProd = (key, val) => setProduction((p) => ({ ...p, [key]: val }));
  const setDeliv = (i, val) => setDeliverables((list) => list.map((d, idx) => (idx === i ? val : d)));
  const addDeliv = () => setDeliverables((list) => {
    const last = list[list.length - 1];
    const next = last ? { ...last, id: crypto.randomUUID() } : emptyDeliverable();
    return [...list, next];
  });
  const removeDeliv = (i) => setDeliverables((list) => list.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const productionPayload = {
        ...production,
        shoot_dates: shootDates.split(',').map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API_URL}/public/briefs/${token}/respond`, {
        submitted_by_name: brief?.contact_name || null,
        submitted_by_email: brief?.contact_email || null,
        production: productionPayload,
        deliverables: deliverables.filter((d) => d.name || (d.aspect_ratios || []).length || (d.platforms || []).length || d.resolution || d.duration),
      });
      setSubmitted(true);
    } catch (e) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-12 h-12 mx-auto text-moss mb-4" />
          <h1 className="record-title mb-2">Thank you!</h1>
          <p className="text-muted-foreground text-[18px]">Your brief for <span className="font-medium text-foreground">{brief?.project_name}</span> has been submitted. The team will be in touch shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground text-[18px] leading-relaxed flex flex-col" data-testid="public-brief-form">
      <div className="max-w-5xl w-full mx-auto px-4 pt-10 md:pt-16 pb-20 md:pb-24 space-y-12 md:space-y-20">
        <header className="space-y-6">
        <div className="flex items-center gap-3">
          {brief?.business_logo ? (
            <img src={brief.business_logo} alt={brief?.business_name || 'Logo'} className="h-12 max-w-[180px] object-contain" data-testid="public-business-logo" />
          ) : brief?.business_name ? (
            <span className="text-lg">{brief.business_name}</span>
          ) : null}
          <div className={brief?.business_logo || brief?.business_name ? 'border-l border-line pl-3 min-w-0' : 'min-w-0'}>
            <p className="eyebrow">project brief</p>
            <h1 className="page-title break-words">{brief?.project_name}</h1>
          </div>
        </div>
        <p className="text-muted-foreground max-w-prose">
          Please fill in as much detail as you can about your production. Your answers help us prepare an accurate quote.
        </p>
        </header>

        {(brief?.client || brief?.contact_name || brief?.contact_email) && (
          <Card>
            <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6" data-testid="public-client-details">
              {brief?.client && (
                <div>
                  <p className="eyebrow">client</p>
                  <p>{brief.client}</p>
                </div>
              )}
              {brief?.contact_name && (
                <div>
                  <p className="eyebrow">contact</p>
                  <p>{brief.contact_name}</p>
                </div>
              )}
              {brief?.contact_email && (
                <div>
                  <p className="eyebrow">email</p>
                  <p className="break-all">{brief.contact_email}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><p className="eyebrow">the shoot</p><CardTitle className="section-title">Production details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Shoot dates</Label>
              <Input
                className={FIELD}
                value={shootDates}
                onChange={(e) => setShootDates(e.target.value)}
                placeholder="Comma-separated, e.g. 12 Aug, 13 Aug"
                data-testid="public-field-shoot_dates"
              />
            </div>
            {CLIENT_FACING_FIELDS.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label>{f.label}</Label>
                <Textarea
                  className={FIELD}
                  value={production[f.key] || ''}
                  onChange={(e) => setProd(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  rows={3}
                  data-testid={`public-field-${f.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <p className="eyebrow">after the shoot</p>
            <CardTitle className="section-title">Deliverables</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {deliverables.map((d, i) => {
              const set = (field, value) => setDeliv(i, { ...d, [field]: value });
              return (
                <div key={d.id} className="border border-line rounded-cards p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Deliverable {i + 1}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeDeliv(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="space-y-1.5 sm:w-[15%]">
                        <Label className="text-xs">Edit length</Label>
                        <Select value={d.duration || ''} onValueChange={(v) => set('duration', v)}>
                          <SelectTrigger className={FIELD}><SelectValue placeholder="Length" /></SelectTrigger>
                          <SelectContent>
                            {EDIT_LENGTHS.map((len) => <SelectItem key={len} value={len}>{formatEditLength(len)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5 flex-1">
                        <Label className="text-xs">Edit name / output label</Label>
                        <Input className={FIELD} value={d.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Hero Film" />
                      </div>
                      <div className="space-y-1.5 flex-1">
                        <Label className="text-xs">Aspect ratios</Label>
                        <CheckboxGroup options={ASPECT_RATIOS} value={d.aspect_ratios || []} onChange={(v) => set('aspect_ratios', v)} allowCustom={false} />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="space-y-1.5 sm:w-[60%]">
                        <Label className="text-xs">Platforms</Label>
                        <CheckboxGroup options={DELIVERABLE_PLATFORMS} value={d.platforms || []} onChange={(v) => set('platforms', v)} allowCustom={false} />
                      </div>
                      <div className="space-y-1.5 flex-1">
                        <Label className="text-xs">Notes</Label>
                        <Input className={FIELD} value={d.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Specifics" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <Button onClick={addDeliv} data-testid="public-add-deliverable">
              <Plus className="w-4 h-4" /> Add deliverable
            </Button>
          </CardContent>
        </Card>

      </div>

      {/* The page ends in the one dark band, with the submit in it */}
      <footer className="mt-auto bg-inverse text-inverse-foreground rounded-t-[60px] md:rounded-t-curtain px-4 pt-14 md:pt-20 pb-14 md:pb-16">
        <div className="max-w-5xl mx-auto flex flex-col items-center text-center gap-6">
          <Logo onInverse className="w-8 h-8" />
          <p className="text-inverse-muted max-w-prose">When you're happy with your answers, send them back to the team.</p>
          <Button
            className="w-full sm:w-auto sm:min-w-[280px] bg-inverse-foreground text-inverse hover:bg-inverse-foreground/90"
            size="lg"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="public-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit brief
          </Button>
        </div>
      </footer>
    </div>
  );
}
